/**
 * The claim workbench: one claim, held together.
 *
 * The redline is the centrepiece, because it is how the claim will be read at
 * the bargaining table: not two blocks of text but one text with the
 * deletions struck and the insertions marked. Everything else — rationale,
 * cost, comparators, the objections it has to survive, the employer position
 * log — is the case for that redline.
 *
 * Two things about the way it is set.
 *
 * **Sections are separated by a rule, not by a gap.** They were `mt-6` and
 * nothing else, so five headings that each name a different kind of thing —
 * proposed words, an argument, a number, a quote, a dated record — ran
 * together as one column of text. §7 sets three rule weights and reserves the
 * strong one for a section boundary; this is what it is for.
 *
 * **Colour is spelled in classes, not in `style` objects.** Every rule and
 * muted colour on this surface was an inline `style={{ borderColor: 'var(…)' }}`,
 * which names the right token and still lets the padding beside it drift,
 * because nothing about an inline style makes two surfaces agree. Ask draws
 * the same borders with `border-[var(--color-rule)]`, so this does too.
 */

import { Fragment, useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { redline } from '@tmea/shared';
import { DraftWithAI } from './AiDraft.js';
import { Objections } from './Objections.js';
import { isUntouched, savedWordingStamp, type MachineDraft } from './provenance.js';
import { ExportSelection } from './ExportSelection.js';
import { ClaimsPaper } from './ClaimsPaper.js';
import { readCostMethod } from './cost.js';
import { api, type ClaimDetail, type LedgerClaim } from '../lib/api.js';

const TYPE_LABELS: Record<string, string> = {
  protect: 'Protect',
  improve: 'Improve',
  new: 'New',
  correct: 'Correct',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  group_endorsed: 'Endorsed',
  tabled: 'Tabled',
  employer_responded: 'Employer responded',
  agreed: 'Agreed',
  parked: 'Parked',
  withdrawn: 'Withdrawn',
};

/** The pipeline, in order, for the status strip. */
const PIPELINE = ['draft', 'group_endorsed', 'tabled', 'employer_responded', 'agreed'] as const;

/**
 * The three treatments every control on this surface uses.
 *
 * Written out longhand each time, the save buttons had drifted to three
 * paddings and two radii within one scrolling column — which is visible the
 * moment two sections are open at once, and was invisible to whoever wrote the
 * second one.
 */
const SECTION = 'section-rule mt-7 pt-5';
const PRIMARY = 'hit shrink-0 rounded-[6px] px-3 py-1.5 text-[12.5px] disabled:opacity-40';
const PRIMARY_INK = { background: 'var(--color-ink)', color: 'var(--color-paper)' };
const FIELD =
  'w-full rounded-[6px] border border-[var(--color-rule-strong)] px-2.5 py-2 text-[14px] outline-none';

export function ClaimWorkbench({
  isRep,
  onOpenClause,
}: {
  isRep: boolean;
  onOpenClause: (number: string) => void;
}) {
  const claims = useQuery({ queryKey: ['ledger-claims'], queryFn: api.ledgerClaims });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [paper, setPaper] = useState(false);
  const boardClient = useQueryClient();

  const rows = claims.data?.claims ?? [];

  // Opening the mode lands on the highest-ranked claim rather than a blank
  // pane. Derived, not synced through an effect: the effect form rendered a
  // blank frame first and kept pointing at a claim after it was removed.
  const activeId =
    selectedId && rows.some((r) => r.id === selectedId) ? selectedId : (rows[0]?.id ?? null);

  if (paper) return <ClaimsPaper onClose={() => setPaper(false)} />;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        The export sits above the split, not inside either half, because it is
        not about the claim on screen — /export takes every claim the reader can
        see and refuses the lot if none has been endorsed. Put beside one claim
        it would read as exporting that claim.
      */}
      <header className="flex shrink-0 flex-wrap items-baseline justify-between gap-4 border-b border-[var(--color-rule)] px-4 py-2.5">
        <h1 className="text-[12.5px] font-semibold">Claims — {rows.length}</h1>
        {/*
          Two exports, and the working copy comes first because it is the one
          used weekly. The gated one refused to produce anything at all while
          any claim carried unchecked wording, which meant the board could not
          be printed for the group to read together — and reading it together is
          how wording gets checked in the first place.
        */}
        <div className="flex flex-wrap items-baseline gap-5">
          {/*
            First, and a button rather than a link, because this is the one a
            representative actually wants: the whole board as a paper they can
            print, hand round a table, and argue over. The two markdown exports
            below it are for a different job — moving the text somewhere else.
          */}
          <button
            type="button"
            onClick={() => setPaper(true)}
            className="hit rounded-[6px] px-3 py-1.5 text-[12px]"
            style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
          >
            Print the claims as a paper
          </button>
          <ExportSelection kind="claims-working" label="Working copy as text" />
          <ExportSelection kind="claims" label="Export to send to the school" />
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <nav
          className="w-[280px] shrink-0 overflow-y-auto border-r border-[var(--color-rule)]"
          aria-label="Claims"
        >
          {rows.map((claim) => (
            <ClaimListRow
              key={claim.id}
              claim={claim}
              selected={claim.id === activeId}
              onSelect={() => setSelectedId(claim.id)}
            />
          ))}
        </nav>

        <div className="screen min-w-0 flex-1">
          {/* Keyed by claim, so switching claims remounts every section. The
              edit drafts below seed useState from props; without the key,
              react-query's cache serves claim B instantly, the sections never
              unmount, and Save would write claim A's rationale onto B. */}
          {activeId ? (
            <ClaimDetailView
              key={activeId}
              id={activeId}
              isRep={isRep}
              onOpenClause={onOpenClause}
              /* Clear the selection first: the deleted claim is the one on
                 screen, and re-rendering it against a 404 shows "Loading…"
                 forever. */
              onDeleted={() => {
                setSelectedId(null);
                void boardClient.invalidateQueries({ queryKey: ['ledger-claims'] });
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ClaimListRow({
  claim,
  selected,
  onSelect,
}: {
  claim: LedgerClaim;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      aria-current={selected}
      className="hit block w-full border-b border-[var(--color-rule)] px-3 py-2.5 text-left"
      style={selected ? { background: 'var(--color-subtle)' } : undefined}
    >
      <span className="flex items-baseline gap-2">
        <span className="clause-number text-[11px]">{claim.ref}</span>
        <span className="label ml-auto">{STATUS_LABELS[claim.status] ?? claim.status}</span>
      </span>
      <span className="mt-0.5 block text-[12.5px] leading-snug">{claim.title}</span>
    </button>
  );
}

/**
 * Removing a claim, behind one confirmation.
 *
 * Claims arrive from Ask as well as by hand, and some of them arrive as
 * questions — C16, C17 and C18 are literally the sentences somebody typed into
 * the ask box. A board that cannot be tidied stops being read, and a rep
 * scanning thirty-four rows for the eight that matter is a rep who stops
 * scanning.
 *
 * One confirmation, naming the ref, and no undo. A dialog that has to be
 * dismissed twice trains people to click through both; a claim carries a
 * reference somebody may have written on paper, so the confirmation says which
 * one is about to go rather than asking "are you sure".
 *
 * The route refuses to delete anything the school has engaged with, and the
 * refusal is shown here rather than swallowed — being told to withdraw instead
 * is the useful half of that answer.
 */
function DeleteClaim({ claim, onDeleted }: { claim: ClaimDetail; onDeleted: () => void }) {
  const [asking, setAsking] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.deleteClaim(claim.id),
    onSuccess: () => {
      setAsking(false);
      onDeleted();
    },
  });

  if (!asking) {
    return (
      <button
        type="button"
        onClick={() => setAsking(true)}
        className="hit label shrink-0 underline decoration-[var(--color-rule-strong)] underline-offset-2"
      >
        Delete
      </button>
    );
  }

  return (
    <span className="flex shrink-0 flex-wrap items-baseline gap-2">
      <span className="label">Delete {claim.ref}?</span>
      <button
        type="button"
        onClick={() => remove.mutate()}
        disabled={remove.isPending}
        className="hit label underline decoration-[var(--color-rule-strong)] underline-offset-2"
      >
        {remove.isPending ? 'Deleting…' : 'Yes, delete it'}
      </button>
      <button type="button" onClick={() => setAsking(false)} className="hit label underline">
        Keep it
      </button>
      {remove.isError ? (
        <span className="label" style={{ color: 'var(--color-ink)' }}>
          {(remove.error as Error).message}
        </span>
      ) : null}
    </span>
  );
}

function ClaimDetailView({
  id,
  isRep,
  onOpenClause,
  onDeleted,
}: {
  id: string;
  isRep: boolean;
  onOpenClause: (number: string) => void;
  onDeleted: () => void;
}) {
  const client = useQueryClient();
  const claim = useQuery({ queryKey: ['claim', id], queryFn: () => api.claim(id) });
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['claim', id] });
    void client.invalidateQueries({ queryKey: ['ledger-claims'] });
  };

  if (claim.isPending) return <p className="label">Loading…</p>;
  if (!claim.data) return null;
  const c = claim.data;

  return (
    <article className="screen-column">
      <header>
        <div className="flex items-baseline gap-3">
          <span className="clause-number text-[17px]">{c.ref}</span>
          {/* Editorial weight, as Ask gives its opening: this is the name of
              the thing the whole column is about. */}
          <h1 className="register-reading m-0 flex-1 text-[21px] leading-[1.3]">{c.title}</h1>
          {isRep ? <DeleteClaim claim={c} onDeleted={onDeleted} /> : null}
        </div>

        <div className="mt-2.5 flex flex-wrap items-baseline gap-3">
          <span className="label">{TYPE_LABELS[c.type] ?? c.type}</span>
          <StatusStrip status={c.status} />
          {c.owner ? <span className="label">owned by {c.owner}</span> : null}
          {c.clauses.map((cl) => (
            <button
              key={cl.number}
              onClick={() => onOpenClause(cl.number)}
              className="hit clause-number text-[11px] underline decoration-[var(--color-rule-strong)] underline-offset-2"
              title={cl.heading ?? cl.number}
            >
              {cl.number}
            </button>
          ))}
        </div>
      </header>

      <Redline claim={c} isRep={isRep} onSaved={refresh} />
      <Rationale claim={c} isRep={isRep} onSaved={refresh} />
      <Cost claim={c} isRep={isRep} onSaved={refresh} />
      <Comparators claim={c} />
      <Objections claim={c} isRep={isRep} onSaved={refresh} />
      <Positions claim={c} isRep={isRep} onSaved={refresh} />
    </article>
  );
}

function StatusStrip({ status }: { status: string }) {
  const reached = PIPELINE.indexOf(status as (typeof PIPELINE)[number]);
  if (reached < 0) {
    // Parked and withdrawn sit outside the pipeline.
    return <span className="label">{STATUS_LABELS[status] ?? status}</span>;
  }
  return (
    <span className="flex items-baseline gap-1" aria-label={`Status: ${STATUS_LABELS[status]}`}>
      {/*
        Steps not yet reached are muted, not `--color-rule-strong`. That token
        is a hairline colour — #b9b9b6 on paper is 1.89:1, which is legible as
        a one-pixel border and not as nine-pixel text. Muted is 4.94:1 and
        clears AA at this size.

        Weight carries where-we-are, not just colour. Reached and unreached
        differed only in lightness, so the answer to "which step is this claim
        on" was a shade — and a shade is not something you can rely on at 9px,
        on a projector in a staffroom, or with the contrast turned down. The
        emphasis on the current step says it a second way. axe cannot see this
        one; it is here because the contrast failure led to the element.
      */}
      {PIPELINE.map((step, index) => (
        <span
          key={step}
          className={`label ${index === reached ? 'font-medium' : ''} ${
            index <= reached ? 'text-[var(--color-ink)]' : 'text-[var(--color-muted)]'
          }`}
        >
          {index > 0 ? '→ ' : ''}
          {STATUS_LABELS[step]}
        </span>
      ))}
    </span>
  );
}

// ─────────────────────────────── The redline

function Redline({
  claim,
  isRep,
  onSaved,
}: {
  claim: ClaimDetail;
  isRep: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(claim.proposedWording ?? '');
  const [machine, setMachine] = useState<MachineDraft | null>(null);
  // Off by default: the diff is for checking replacement drafting, which is the
  // uncommon case and never the first thing a reader needs.
  const [showDiff, setShowDiff] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.updateClaim2(claim.id, {
        proposedWording: draft || null,
        /**
         * Sent only when this save carries a machine draft, because the column
         * is what `claims_blocking_export()` in migration 0009 reads to hold a
         * claim back until someone has checked the wording word for word.
         *
         * Omitted on an ordinary edit rather than cleared. A rep who rewrites
         * an AI draft by hand has improved it, not laundered it; the gate lifts
         * when they press "I have checked it word for word", which is a
         * statement about the words, not about who typed them.
         */
        ...(machine ? { proposedWordingAi: machine.text } : {}),
      }),
    onSuccess: () => {
      setEditing(false);
      setMachine(null);
      onSaved();
    },
  });
  const verify = useMutation({
    mutationFn: () => api.verifyWording(claim.id),
    onSuccess: onSaved,
  });

  const segments = useMemo(
    () =>
      claim.currentVerbatim && claim.proposedWording
        ? redline(claim.currentVerbatim, claim.proposedWording)
        : null,
    [claim.currentVerbatim, claim.proposedWording],
  );

  return (
    <section className={SECTION}>
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="label m-0">What we want</h2>
        {claim.wordingVerifiedAt ? (
          <span className="stamp stamp-verified">wording checked by {claim.wordingVerifiedBy}</span>
        ) : savedWordingStamp(claim) ? (
          /*
            Three states, not two. "Not yet checked" said the same thing about
            wording a rep typed and wording a model produced, and those are not
            the same risk: one is a person's own sentence awaiting a second
            reader, the other has never been anybody's sentence at all. The
            column has held the machine draft since migration 0006 and nothing
            had ever read it.
          */
          <span className="stamp stamp-ai">{savedWordingStamp(claim)}</span>
        ) : null}
      </div>

      {claim.currentVerbatim ? (
        <blockquote className="register-reading measure mt-2 border-l border-[var(--color-rule)] pl-3 text-[14px] leading-relaxed text-[var(--color-muted)]">
          {/* The snapshot, not the live clause: evidence never mutates. If a
              redraft changes the clause, this stays what the group objected
              to, and the linked clause chip shows the current text. */}
          {claim.currentVerbatim}
        </blockquote>
      ) : (
        <p className="mt-2 text-[12.5px] text-[var(--color-muted)]">
          No verbatim snapshot — this claim proposes new words rather than amending existing ones.
        </p>
      )}

      {/*
        What the group wants, in the group's own words, shown plainly.

        This used to render the diff of the proposal against the clause, always,
        and the result on C1 was six hundred words of interleaved strike-through
        that nobody could read on paper or on screen. The mistake was assuming a
        claim is always replacement drafting. It usually is not: the lawyers
        rewrite, and what the teachers on the team are doing is saying what they
        want and why. A short demand diffed against a whole clause renders as the
        clause being deleted and a sentence appearing, which is worse than
        useless — it looks like a legal instruction and reads like damage.

        So the demand is shown as itself, the clause it changes is quoted above
        it, and the redline is behind a toggle for the case it was built for:
        somebody has written the replacement words and wants to see them against
        the original.
      */}
      {/*
        Falls back to the machine draft, because a claim whose only wording sits
        in proposed_wording_ai showed nothing at all here. C15 carries a fully
        drafted clause — five days of paid delegate training, written to match
        the Award — and this section was blank for it: the wording existed,
        blocked the export, and could not be read by the person being asked to
        check it. The stamp above already says who wrote it.
      */}
      {claim.proposedWording ?? claim.proposedWordingAi ? (
        <div className="register-reading measure mt-3 whitespace-pre-line text-[15px] leading-relaxed">
          {claim.proposedWording ?? claim.proposedWordingAi}
        </div>
      ) : null}

      {segments ? (
        <button
          type="button"
          onClick={() => setShowDiff((v) => !v)}
          className="hit label mt-3 block underline decoration-[var(--color-rule-strong)] underline-offset-2"
          aria-expanded={showDiff}
        >
          {showDiff ? 'Hide the redline' : 'Show it as a redline against the clause'}
        </button>
      ) : null}

      {segments && showDiff ? (
        <div className="register-reading measure mt-3 text-[15px] leading-relaxed" aria-label="Redline">
          {segments.map((segment, index) =>
            segment.kind === 'same' ? (
              <span key={index}>{segment.text}</span>
            ) : segment.kind === 'del' ? (
              <del key={index} className="text-[var(--color-muted)]">
                {segment.text}
              </del>
            ) : (
              // Insertions are weight and underline, never colour alone: the
              // redline must survive a monochrome print handed across a table.
              <ins key={index} style={{ fontWeight: 600, textDecorationStyle: 'dotted' }}>
                {segment.text}
              </ins>
            ),
          )}
        </div>
      ) : null}

      {isRep ? (
        editing ? (
          <div className="mt-3">
            <textarea
              autoFocus
              rows={6}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className={`${FIELD} measure`}
              style={{ fontFamily: 'var(--font-serif)' }}
              aria-label="Proposed wording"
            />
            <DraftWithAI
              claimId={claim.id}
              field="wording"
              value={draft}
              machine={machine}
              onDrafted={(drafted) => {
                setMachine(drafted);
                setDraft(drafted.text);
              }}
            />
            <div className="mt-2.5 flex items-center gap-3">
              <button onClick={() => save.mutate()} className={PRIMARY} style={PRIMARY_INK}>
                {/*
                  Named for what saving means when the words are still the
                  model's: the claim starts proposing them to the employer. It
                  stays available — refusing to save unread text would just move
                  the rep to a text editor and back — but it does not pretend
                  this is the same act as saving a sentence they wrote.
                */}
                {isUntouched(draft, machine) ? 'Save the software draft as it is' : 'Save wording'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="hit label underline underline-offset-2"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-2.5 flex flex-wrap items-center gap-3">
            <button
              onClick={() => {
                setDraft(claim.proposedWording ?? claim.currentVerbatim ?? '');
                setEditing(true);
              }}
              className="hit label underline underline-offset-2"
            >
              {claim.proposedWording ? 'Edit the proposed wording' : 'Draft the proposed wording'}
            </button>
            {claim.proposedWording && !claim.wordingVerifiedAt ? (
              <button
                onClick={() => verify.mutate()}
                className="hit label underline underline-offset-2"
              >
                I have checked it word for word
              </button>
            ) : null}
          </div>
        )
      ) : null}
    </section>
  );
}

// ─────────────────────────────── Rationale and cost

function Rationale({
  claim,
  isRep,
  onSaved,
}: {
  claim: ClaimDetail;
  isRep: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(claim.rationale ?? '');
  const [machine, setMachine] = useState<MachineDraft | null>(null);
  const save = useMutation({
    /**
     * The rationale has no `_ai` column and does not get one here.
     *
     * `proposed_wording_ai` exists because the wording is the thing that goes
     * to the employer verbatim and has a database gate reading it. The
     * rationale's protection is that a generation is never written by the
     * server at all — it lives in this editor, stamped, until a person presses
     * this button. Saving is the act that makes the words theirs, which is
     * exactly the line the product rule draws: visibly a draft until a person
     * edits or accepts it.
     */
    mutationFn: () => api.updateClaim2(claim.id, { rationale: draft || null }),
    onSuccess: () => {
      setEditing(false);
      setMachine(null);
      onSaved();
    },
  });

  return (
    <section className={SECTION}>
      <h2 className="label m-0">Why</h2>
      {editing ? (
        <div className="mt-2">
          <textarea
            autoFocus
            rows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className={`${FIELD} measure`}
            style={{ fontFamily: 'var(--font-serif)' }}
            aria-label="Rationale"
          />
          <DraftWithAI
            claimId={claim.id}
            field="rationale"
            value={draft}
            machine={machine}
            onDrafted={(drafted) => {
              setMachine(drafted);
              setDraft(drafted.text);
            }}
          />
          <div className="mt-2.5 flex items-center gap-3">
            <button onClick={() => save.mutate()} className={PRIMARY} style={PRIMARY_INK}>
              {/*
                A rationale is exported as the case for the claim, in the
                group's voice, with no verbatim clause beside it to contradict
                it. Saving unedited machine prose into that slot is allowed and
                is not called the same thing as saving your own sentence.
              */}
              {isUntouched(draft, machine) ? 'Save the software draft as it is' : 'Save'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="hit label underline underline-offset-2"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          {claim.rationale ? (
            <p className="register-reading measure mt-2 text-[14px] leading-relaxed">
              {claim.rationale}
            </p>
          ) : (
            <p className="mt-2 text-[12.5px] text-[var(--color-muted)]">
              No rationale drafted yet.
            </p>
          )}
          {isRep ? (
            <button
              onClick={() => setEditing(true)}
              className="hit label mt-2 underline underline-offset-2"
            >
              {claim.rationale ? 'Edit' : 'Draft it'}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}

function Cost({
  claim,
  isRep,
  onSaved,
}: {
  claim: ClaimDetail;
  isRep: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(claim.costPerFteYear ?? '');
  const [method, setMethod] = useState(claim.costMethod ?? '');
  const [confidence, setConfidence] = useState(claim.costConfidence ?? 'low');

  const save = useMutation({
    mutationFn: () =>
      api.updateClaim2(claim.id, {
        costPerFteYear: amount === '' ? null : Number(amount),
        costMethod: method || null,
        costConfidence: (confidence || null) as 'low' | 'medium' | 'high' | null,
      }),
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
  });

  return (
    <section className={SECTION}>
      <h2 className="label m-0">What it costs</h2>

      {editing ? (
        <div className="mt-2 max-w-md">
          <label className="label" htmlFor="cost-amount">
            Per full-time teacher, per year (dollars)
          </label>
          <input
            id="cost-amount"
            type="number"
            min="0"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={`${FIELD} clause-number mt-1`}
          />
          <label className="label mt-3 block" htmlFor="cost-method">
            How that was worked out
          </label>
          <textarea
            id="cost-method"
            rows={2}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className={`${FIELD} mt-1 text-[13px]`}
          />
          <label className="label mt-3 block" htmlFor="cost-confidence">
            Confidence
          </label>
          <select
            id="cost-confidence"
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
            className="hit mt-1 rounded-[5px] border border-[var(--color-rule-strong)] bg-transparent px-2 py-1.5 text-[13px] outline-none"
          >
            <option value="low">Low — rough figure</option>
            <option value="medium">Medium — reasonable inputs</option>
            <option value="high">High — checked against payroll figures</option>
          </select>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={() => save.mutate()} className={PRIMARY} style={PRIMARY_INK}>
              Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="hit label underline underline-offset-2"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          {claim.costPerFteYear !== null ? (
            <p className="mt-2 flex flex-wrap items-baseline gap-2">
              {/*
                The figure in mono, at the size the mockup sets for a headline
                number. A cost is data, and it was rendering in the same serif
                as the argument beside it — which is the one place in this
                product where a number and a sentence must not look alike.
              */}
              <span className="clause-number text-[24px] font-semibold tracking-tight">
                ${Number(claim.costPerFteYear).toLocaleString('en-AU')}
              </span>
              <span className="label">
                per FTE per year
                {claim.costConfidence ? ` · ${claim.costConfidence} confidence` : ''}
              </span>
            </p>
          ) : claim.costMethod ? (
            /*
             * Working, but no figure yet — and previously this said both.
             *
             * The empty state rendered whenever there was no amount, and the
             * method rendered unconditionally underneath it, so a claim with
             * reasoning but no number told a representative "Not costed" and
             * then showed them the costing directly below. Two statements, one
             * of them false, three lines apart.
             *
             * They are different states and they now read differently. Somebody
             * has thought about what this costs and has not committed to a
             * number, which is a normal place to be and worth saying plainly,
             * because it is also the state where the employer's figure is the
             * only one in the room.
             */
            <p className="measure mt-2 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
              Worked out but not yet costed to a figure. Until there is one, the employer's number
              is the only number.
            </p>
          ) : (
            <p className="measure mt-2 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
              Not costed. A claim with no cost estimate gets costed by the employer instead, on
              their numbers.
            </p>
          )}
          {claim.costMethod ? <CostMethod text={claim.costMethod} /> : null}
          {isRep ? (
            <button
              onClick={() => setEditing(true)}
              className="hit label mt-2 underline underline-offset-2"
            >
              {claim.costPerFteYear !== null ? 'Edit' : 'Cost it'}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * The costing, rendered as prose or not at all.
 *
 * The reading is in `readCostMethod`, tested there. This only decides how each
 * outcome is set: prose as a paragraph, a recognised structure as its sentence
 * plus a figure list, and an unreadable one as nothing at all.
 */
function CostMethod({ text }: { text: string }): ReactElement | null {
  const view = readCostMethod(text);
  if (view === null) return null;

  if (view.kind === 'prose') {
    return (
      <p className="measure mt-1.5 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
        {view.text}
      </p>
    );
  }

  return (
    <div className="measure mt-1.5 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
      {view.method ? <p className="m-0">{view.method}</p> : null}
      {view.inputs.length > 0 ? (
        <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          {view.inputs.map(([label, value]) => (
            <Fragment key={label}>
              <dt className="label">{label}</dt>
              {/* Figures in mono, for the same reason the headline is: on this
                  surface a number and a sentence must not look alike. */}
              <dd className="clause-number m-0">{value}</dd>
            </Fragment>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

// ─────────────────────────────── Justification and the employer's record

function Comparators({ claim }: { claim: ClaimDetail }) {
  if (claim.comparators.length === 0) return null;
  return (
    <section className={SECTION}>
      <h2 className="label m-0">What others already have</h2>
      {claim.comparators.map((cmp) => (
        <figure key={cmp.id} className="measure mt-3">
          <blockquote className="register-reading border-l border-[var(--color-rule)] pl-3 text-[14px] leading-relaxed">
            {cmp.quote}
          </blockquote>
          <figcaption className="label mt-1.5 pl-3">
            {cmp.instrument} · {cmp.citation}
            {cmp.note ? ` — ${cmp.note}` : ''}
          </figcaption>
        </figure>
      ))}
    </section>
  );
}

function Positions({
  claim,
  isRep,
  onSaved,
}: {
  claim: ClaimDetail;
  isRep: boolean;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [position, setPosition] = useState('');
  const [quote, setQuote] = useState('');
  const [source, setSource] = useState('');

  const log = useMutation({
    mutationFn: () =>
      api.logPosition(claim.id, {
        occurredOn: date,
        position,
        ...(quote.trim() ? { verbatimQuote: quote } : {}),
        ...(source.trim() ? { source } : {}),
      }),
    onSuccess: () => {
      setOpen(false);
      setDate('');
      setPosition('');
      setQuote('');
      setSource('');
      onSaved();
    },
  });

  return (
    <section className={SECTION}>
      <h2 className="label m-0">What the employer has said</h2>

      {claim.positions.length === 0 ? (
        <p className="measure mt-2 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
          Nothing logged yet. “They said they’d look at it” is worth nothing in month four; a
          dated quote is leverage.
        </p>
      ) : (
        <ol className="card mt-2">
          {claim.positions.map((p) => (
            <li key={p.id} className="border-b border-[var(--color-rule)] py-2.5 first:pt-0 last:border-b-0 last:pb-0">
              <div className="flex items-baseline gap-3">
                <span className="clause-number w-14 shrink-0 text-[11px] text-[var(--color-muted)]">
                  {new Date(p.occurredOn).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: '2-digit',
                  })}
                </span>
                <p className="register-reading m-0 text-[14px] leading-relaxed">{p.position}</p>
              </div>
              {p.verbatimQuote ? (
                <blockquote className="register-reading mt-1.5 ml-[68px] border-l border-[var(--color-rule)] pl-3 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
                  “{p.verbatimQuote}”
                </blockquote>
              ) : null}
              <p className="label mt-1.5 ml-[68px]">
                {p.source ? `${p.source} · ` : ''}logged by {p.loggedBy}
              </p>
            </li>
          ))}
        </ol>
      )}

      {isRep ? (
        open ? (
          <form
            className="card measure mt-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (date && position.trim().length >= 3) log.mutate();
            }}
          >
            <div className="card-header">
              <p className="label m-0">Log a position</p>
            </div>
            <label className="label" htmlFor="pos-date">
              When
            </label>
            <input
              id="pos-date"
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="hit mt-1 block rounded-[5px] border border-[var(--color-rule-strong)] bg-transparent px-2 py-1.5 text-[13px] outline-none"
            />
            <label className="label mt-3 block" htmlFor="pos-position">
              Their position, in a sentence
            </label>
            <input
              id="pos-position"
              required
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              className={`${FIELD} mt-1 text-[13px]`}
            />
            <label className="label mt-3 block" htmlFor="pos-quote">
              Their exact words, if you have them
            </label>
            <textarea
              id="pos-quote"
              rows={2}
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              className={`${FIELD} mt-1 text-[13px]`}
              style={{ fontFamily: 'var(--font-serif)' }}
            />
            <label className="label mt-3 block" htmlFor="pos-source">
              Where — meeting, email, corridor
            </label>
            <input
              id="pos-source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className={`${FIELD} mt-1 text-[13px]`}
            />
            <div className="mt-3 flex items-center gap-3">
              <button type="submit" className={PRIMARY} style={PRIMARY_INK}>
                Log it
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="hit label underline underline-offset-2"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setOpen(true)}
            className="hit label mt-2 underline underline-offset-2"
          >
            Log a position
          </button>
        )
      ) : null}
    </section>
  );
}
